import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { DeliverableRevision } from '../types/deliverable';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface DeliverableRevisionHistoryProps {
  deliverableId: string;
}

export const DeliverableRevisionHistory: React.FC<DeliverableRevisionHistoryProps> = ({
  deliverableId,
}) => {
  const [revisions, setRevisions] = useState<DeliverableRevision[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!deliverableId) return;

    const fetchRevisions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/deliverables/${deliverableId}/revisions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch revisions');
        const result = await response.json();
        setRevisions(result.data || result || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load revisions');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRevisions();
  }, [deliverableId]);

  if (isLoading) {
    return (
      <div className="py-4 text-sm text-gray-500 text-center">Loading...</div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-sm text-red-500 text-center">{error}</div>
    );
  }

  if (revisions.length === 0) {
    return (
      <div className="py-4 text-sm text-gray-500 text-center">No revisions yet</div>
    );
  }

  return (
    <div className="space-y-3">
      {revisions.map((revision) => (
        <div
          key={revision.id}
          className="pl-4 border-l-2 border-indigo-200"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
              Rev #{revision.revisionNumber}
            </span>
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">
              {new Date(revision.rejectedAt).toLocaleDateString()}
            </span>
          </div>
          <p className="text-sm text-gray-700">
            {revision.rejectionReason || 'No reason provided'}
          </p>
          {revision.rejectedById && (
            <p className="text-xs text-gray-400 mt-0.5">
              Rejected by: {revision.rejectedById}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default DeliverableRevisionHistory;
